import i18next from 'i18next';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { debounce } from 'lodash';
import { useAuthFetchFromRestAPI } from '../../util/api';
import PrimaryButton from '../../components/PrimaryButton';
import { EntityScope } from '../../../../shared/utils/enums';
import { useExtendedAuth } from '../../util/auth0';
import { isScopeKey, ManagementMode, ScopeKey } from '../../util/types';
import SearchBar from '../../components/SearchBar';
import { ExerciseTagDto } from '../../../../shared/dtos/exerciseTag.dto';
import { ExerciseDto } from '../../../../shared/dtos/exercise.dto';
import { PagedDto } from '../../../../shared/dtos/paged.dto';
import ExerciseTable from './ExerciseTable';
import ExerciseModal from './ExerciseModal';

const PAGE_SIZE: number = 10;
const SEARCH_DEBOUNCE_MS: number = 400;

type ExerciseVideoManagementProps = {
  mode: ManagementMode;
};

function getModalEntityScope(mode: ManagementMode): EntityScope {
  if (mode === 'superAdmin') {
    return EntityScope.CORE;
  }

  return EntityScope.TENANT;
}

const ExerciseManagement = ({ mode }: ExerciseVideoManagementProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  const { isSuperAdminUser } = useExtendedAuth();

  const [isSuperAdmin, setIsSuperAdmin] = useState(mode === 'superAdmin');
  const [activeKey, setActiveKey] = useState<ScopeKey>('tenant');

  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isLoadingTenantExercises, setIsLoadingTenantExercises] = useState(false);
  const [isLoadingCoreExercises, setIsLoadingCoreExercises] = useState(false);

  const [tenantPage, setTenantPage] = useState(0);
  const [tenantHasMore, setTenantHasMore] = useState(false);
  const [tenantExercises, setTenantExercises] = useState<ExerciseDto[]>([]);

  const [corePage, setCorePage] = useState(0);
  const [coreHasMore, setCoreHasMore] = useState(false);
  const [coreExercises, setCoreExercises] = useState<ExerciseDto[]>([]);

  const [searchKey, setSearchKey] = useState('');
  const [tags, setTags] = useState<ExerciseTagDto[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [editExercise, setEditExercise] = useState<ExerciseDto | null>(null);

  const isAdminMode: boolean = mode === 'admin';
  const modalEntityScope: EntityScope = getModalEntityScope(mode);

  useEffect(() => {
    if (mode === 'superAdmin') {
      setIsSuperAdmin(true);
      return;
    }

    void (async (): Promise<void> => {
      setIsSuperAdmin(await isSuperAdminUser());
    })();
  }, [isSuperAdminUser, mode]);

  const fetchTags: () => Promise<void> = useCallback(async () => {
    setIsLoadingTags(true);

    try {
      if (mode === 'superAdmin') {
        const result: ExerciseTagDto[] = await authFetch('/api/v1/exercise/tag', {
          method: 'GET',
          queryParameters: new URLSearchParams({ onlyCore: 'true' }),
        });

        setTags(Array.isArray(result) ? result : []);
        return;
      }

      const result: ExerciseTagDto[] = await authFetch('/api/v1/exercise/tag', { method: 'GET' });
      setTags(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingTags(false);
    }
  }, [authFetch, mode]);

  const fetchTenantExercises: () => Promise<void> = useCallback(async () => {
    setIsLoadingTenantExercises(true);

    try {
      const body = {
        page: tenantPage,
        pageSize: PAGE_SIZE,
        searchKey: searchKey.trim() || undefined,
      };

      const result: PagedDto<ExerciseDto> = await authFetch('/api/v1/exercise/tenant/search', {
        method: 'POST',
        body,
      });

      setTenantExercises(result.items);
      setTenantHasMore((tenantPage + 1) * PAGE_SIZE < result.totalCount);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingTenantExercises(false);
    }
  }, [authFetch, searchKey, tenantPage]);

  const fetchCoreExercises: () => Promise<void> = useCallback(async () => {
    setIsLoadingCoreExercises(true);

    try {
      const body = {
        page: corePage,
        pageSize: PAGE_SIZE,
        searchKey: searchKey.trim() || undefined,
      };

      const result: PagedDto<ExerciseDto> = await authFetch('/api/v1/exercise/core/search', {
        method: 'POST',
        body,
      });

      setCoreExercises(result.items);
      setCoreHasMore((corePage + 1) * PAGE_SIZE < result.totalCount);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCoreExercises(false);
    }
  }, [authFetch, corePage, searchKey]);

  const debounceSearch = useMemo(
    () =>
      debounce((value: string): void => {
        setSearchKey(value);
        setTenantPage(0);
        setCorePage(0);
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    return (): void => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  useEffect(() => {
    void fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    if (!isAdminMode) {
      return;
    }

    void fetchTenantExercises();
  }, [fetchTenantExercises, isAdminMode]);

  useEffect(() => {
    void fetchCoreExercises();
  }, [fetchCoreExercises]);

  const closeModal = (): void => {
    setEditExercise(null);
    setShowModal(false);
  };

  const openCreateModal = (): void => {
    setEditExercise(null);
    setShowModal(true);
  };

  const openEditModal = (exercise: ExerciseDto): void => {
    setEditExercise(exercise);
    setShowModal(true);
  };

  const refreshEditableExercises = (): void => {
    if (isAdminMode) {
      if (tenantPage === 0) {
        void fetchTenantExercises();
        return;
      }

      setTenantPage(0);
      return;
    }

    if (corePage === 0) {
      void fetchCoreExercises();
      return;
    }

    setCorePage(0);
  };

  const renderLoading = (): ReactElement => {
    return (
      <div className="d-flex justify-content-center py-4">
        <Spinner animation="border" />
      </div>
    )
  }

  const renderSearchBar = (): ReactElement => {
    return (
      <div className="mb-3 mt-3">
        <SearchBar
          onSearch={(value: string) => debounceSearch(value)}
          placeholder={i18next.t('generic.search')}
          maxWidth={400}
        />
      </div>
    )
  }

  const renderPaginationActions = (
    page: number,
    hasMore: boolean,
    isLoading: boolean,
    onPreviousPage: () => void,
    onNextPage: () => void,
    showCreateButton: boolean,
  ): ReactElement => {
    return (
      <div className="d-flex justify-content-end flex-wrap gap-2 mb-3">
        <PrimaryButton
          onClick={onPreviousPage}
          disabled={isLoading || page <= 0}>
          {i18next.t('generic.previousPage')}
        </PrimaryButton>

        <PrimaryButton
          onClick={onNextPage}
          disabled={isLoading || !hasMore}>
          {i18next.t('generic.nextPage')}
        </PrimaryButton>

        {showCreateButton && (
          <PrimaryButton
            onClick={openCreateModal}
            disabled={isLoadingTags}>
            {i18next.t('generic.create')}
          </PrimaryButton>
        )}
      </div>
    )
  };

  const renderExerciseTable = (
    exercises: ExerciseDto[],
    isLoadingExercises: boolean,
    canEdit: boolean,
  ): ReactElement => {
    if (isLoadingTags || isLoadingExercises) {
      return renderLoading();
    }

    return (
      <ExerciseTable
        exercises={exercises}
        tags={tags}
        onClick={canEdit ? openEditModal : undefined}
      />
    );
  };

  const renderTenantTab = (): ReactElement => {
    return (
      <Tab
        eventKey="tenant"
        title={i18next.t('adminExerciseVideo.tenantExercises')}>
        {renderPaginationActions(
          tenantPage,
          tenantHasMore,
          isLoadingTenantExercises,
          () => setTenantPage((previousPage: number) => Math.max(previousPage - 1, 0)),
          () => setTenantPage((previousPage: number) => previousPage + 1),
          isSuperAdmin,
        )}

        {renderExerciseTable(tenantExercises, isLoadingTenantExercises, true)}
      </Tab>
    )
  }

  const renderCoreTab = (): ReactElement => {
    return (
      <Tab
        eventKey="core"
        title={i18next.t('adminExerciseVideo.coreExercises')}>
        {renderPaginationActions(
          corePage,
          coreHasMore,
          isLoadingCoreExercises,
          () => setCorePage((previousPage: number) => Math.max(previousPage - 1, 0)),
          () => setCorePage((previousPage: number) => previousPage + 1),
          false,
        )}

        {renderExerciseTable(coreExercises, isLoadingCoreExercises, false)}
      </Tab>
    )
  };

  const renderAdminContent = (): ReactElement => {
    return (
      <Tabs
        activeKey={activeKey}
        onSelect={(key) => {
          if (isScopeKey(key)) {
            setActiveKey(key);
          }
        }}
        className="mb-3 pt-3"
        justify
        mountOnEnter
        unmountOnExit>
        {renderTenantTab()}
        {renderCoreTab()}
      </Tabs>
    )
  };

  const renderSuperAdminContent = (): ReactElement => {
    return (
      <>
        {renderPaginationActions(
          corePage,
          coreHasMore,
          isLoadingCoreExercises,
          () => setCorePage((previousPage: number) => Math.max(previousPage - 1, 0)),
          () => setCorePage((previousPage: number) => previousPage + 1),
          true,
        )}

        {renderExerciseTable(coreExercises, isLoadingCoreExercises, true)}
      </>
    )
  }
  
  return (
    <>
      <ExerciseModal
        show={showModal}
        entityScope={modalEntityScope}
        tags={tags}
        exerciseVideo={editExercise}
        isSuperAdmin={isSuperAdmin}
        onClose={closeModal}
        onSave={refreshEditableExercises}
      />

      {renderSearchBar()}

      {isAdminMode ? renderAdminContent() : renderSuperAdminContent()}
    </>
  );
};

export default ExerciseManagement;
